import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './invoice.reducer';

export const Invoice = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const invoiceList = useAppSelector(state => state.invoice.entities);
  const loading = useAppSelector(state => state.invoice.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="invoice-heading" data-cy="InvoiceHeading">
        <Translate contentKey="crawlerBackendApp.invoice.home.title">Invoices</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.invoice.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/invoice/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.invoice.home.createLabel">Create new Invoice</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {invoiceList && invoiceList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.invoice.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('sumFixedMonthlyPrice')}>
                  <Translate contentKey="crawlerBackendApp.invoice.sumFixedMonthlyPrice">
                    Sum Fixed Monthly Price
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sumFixedMonthlyPrice')} />
                </th>
                <th className="hand" onClick={sort('startDate')}>
                  <Translate contentKey="crawlerBackendApp.invoice.startDate">Start Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('startDate')} />
                </th>
                <th className="hand" onClick={sort('endDate')}>
                  <Translate contentKey="crawlerBackendApp.invoice.endDate">End Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('endDate')} />
                </th>
                <th className="hand" onClick={sort('invoiceStatus')}>
                  <Translate contentKey="crawlerBackendApp.invoice.invoiceStatus">Invoice Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('invoiceStatus')} />
                </th>
                <th className="hand" onClick={sort('invoiceType')}>
                  <Translate contentKey="crawlerBackendApp.invoice.invoiceType">Invoice Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('invoiceType')} />
                </th>
                <th className="hand" onClick={sort('createdTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.invoice.createdTimestamp">Created Timestamp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdTimestamp')} />
                </th>
                <th className="hand" onClick={sort('invoiceNumber')}>
                  <Translate contentKey="crawlerBackendApp.invoice.invoiceNumber">Invoice Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('invoiceNumber')} />
                </th>
                <th className="hand" onClick={sort('relation')}>
                  <Translate contentKey="crawlerBackendApp.invoice.relation">Relation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('relation')} />
                </th>
                <th className="hand" onClick={sort('description')}>
                  <Translate contentKey="crawlerBackendApp.invoice.description">Description</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('description')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.invoice.agreement">Agreement</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoiceList.map((invoice, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/invoice/${invoice.id}`} color="link" size="sm">
                      {invoice.id}
                    </Button>
                  </td>
                  <td>{invoice.sumFixedMonthlyPrice}</td>
                  <td>
                    {invoice.startDate ? (
                      <TextFormat type="date" value={invoice.startDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {invoice.endDate ? (
                      <TextFormat type="date" value={invoice.endDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.InvoiceStatus.${invoice.invoiceStatus}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.InvoiceType.${invoice.invoiceType}`} />
                  </td>
                  <td>
                    {invoice.createdTimestamp ? (
                      <TextFormat type="date" value={invoice.createdTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{invoice.invoiceNumber}</td>
                  <td>{invoice.relation}</td>
                  <td>{invoice.description}</td>
                  <td>
                    {invoice.agreement ? (
                      <Link to={`/agreement/${invoice.agreement.id}`}>{invoice.agreement.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/invoice/${invoice.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/invoice/${invoice.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/invoice/${invoice.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.invoice.home.notFound">No Invoices found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Invoice;
